import { useState } from "react";
import { createFileRoute, redirect, Link } from "@tanstack/react-router";
import { useAuth } from "../../../components/AuthProvider";
import { createRouteError } from "../../../components/RouteErrorBoundary";
import { PageSkeleton } from "../../../components/RouteSkeleton";
import { useSeasons, useSeasonRankings } from "../../../hooks/useSeasons";
import type { SeasonEntry, SeasonRankingEntry } from "../../../server/seasons";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { queryKeys, STALE_TIMES } from "../../../lib/queryClient";
import { getSeasons } from "../../../server/seasons";
import type { RouterContextWithSession } from "../../../router";

export const Route = createFileRoute("/_admin/seasons/")({
  beforeLoad: async ({ context }) => {
    if (typeof window === "undefined") return {};
    const sb = context.supabase;
    if (!sb) return {};
    const {
      data: { session },
    } = await sb.auth.getSession();
    if (!session) {
      throw redirect({ to: "/auth" });
    }
    return { session };
  },
  loader: async ({ context }) => {
    if (typeof window === "undefined") return;
    const { session } = context as RouterContextWithSession;
    if (!session) return;

    await context.queryClient.ensureQueryData({
      queryKey: queryKeys.seasons.all,
      queryFn: async () => {
        const result = await getSeasons({
          data: { accessToken: session.access_token },
        });
        if (result.error) throw new Error(result.error);
        return result.data;
      },
      staleTime: STALE_TIMES.aggregate,
    });
  },
  pendingComponent: () => <PageSkeleton rows={4} variant="cards" />,
  errorComponent: createRouteError("Seasons"),
  component: SeasonsPage,
});

function SeasonsPage() {
  const { session, loading, isAuthenticated } = useAuth();

  if (loading || !isAuthenticated || !session) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  return <AuthenticatedSeasons accessToken={session.access_token} />;
}

function AuthenticatedSeasons({ accessToken }: { accessToken: string }) {
  const auth = { accessToken };
  const [selectedSeason, setSelectedSeason] = useState<string | null>(null);

  const { data: seasonsData, isLoading: seasonsLoading, error: seasonsError } = useSeasons(auth);
  const { data: rankings, isLoading: rankingsLoading } = useSeasonRankings(auth, selectedSeason);

  const activeSeason = seasonsData?.seasons.find((s: SeasonEntry) => s.is_active);

  return (
    <div className="text-foreground">
      <div className="container mx-auto px-4 py-6 max-w-4xl space-y-6">
        <header className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">Seasons</h1>
            <p className="text-muted-foreground">
              Gamification seasons and competitive leagues
            </p>
          </div>
          <Button variant="outline" asChild>
            <Link to="/gamification">Back to Gamification</Link>
          </Button>
        </header>

        {seasonsLoading && (
          <p className="text-muted-foreground">Loading seasons...</p>
        )}
        {seasonsError && (
          <p className="text-destructive">Error: {seasonsError.message}</p>
        )}

        {!seasonsLoading && !seasonsError && seasonsData && (
          <>
            {/* Active Season */}
            {activeSeason && (
              <Card className="border-primary/50">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <span className="text-primary">●</span>
                    {activeSeason.season_name}
                    <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full ml-2">
                      Active
                    </span>
                  </CardTitle>
                  <CardDescription>
                    Season {activeSeason.season_number} ·{" "}
                    {new Date(activeSeason.start_date).toLocaleDateString()} —{" "}
                    {new Date(activeSeason.end_date).toLocaleDateString()}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="rounded-lg border bg-card p-3">
                      <p className="text-xs text-muted-foreground">Participants</p>
                      <p className="text-lg font-semibold">{activeSeason.total_participants}</p>
                    </div>
                    <div className="rounded-lg border bg-card p-3">
                      <p className="text-xs text-muted-foreground">XP Awarded</p>
                      <p className="text-lg font-semibold">{activeSeason.total_xp_awarded.toLocaleString()}</p>
                    </div>
                  </div>
                  {selectedSeason !== activeSeason.id && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-4"
                      onClick={() => setSelectedSeason(activeSeason.id)}
                    >
                      View Rankings
                    </Button>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Season Rankings */}
            {selectedSeason && (
              <Card>
                <CardHeader>
                  <CardTitle>Season Rankings</CardTitle>
                  <CardDescription>
                    Top performers this season
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {rankingsLoading && (
                    <p className="text-muted-foreground">Loading rankings...</p>
                  )}
                  {!rankingsLoading && (!rankings || rankings.length === 0) && (
                    <p className="text-muted-foreground">No rankings yet.</p>
                  )}
                  {!rankingsLoading && rankings && rankings.length > 0 && (
                    <ol className="space-y-2">
                      {rankings.map((r: SeasonRankingEntry) => (
                        <li
                          key={r.user_id}
                          className="flex items-center justify-between rounded-lg border bg-card p-3"
                        >
                          <span className="font-medium">
                            #{r.rank} {r.full_name || r.username}
                          </span>
                          <span className="text-muted-foreground">
                            {r.season_xp.toLocaleString()} XP · Level {r.season_level}
                          </span>
                        </li>
                      ))}
                    </ol>
                  )}
                </CardContent>
              </Card>
            )}

            {/* All Seasons */}
            <Card>
              <CardHeader>
                <CardTitle>All Seasons</CardTitle>
                <CardDescription>
                  {seasonsData.total} seasons total
                </CardDescription>
              </CardHeader>
              <CardContent>
                {seasonsData.seasons.length === 0 && (
                  <p className="text-muted-foreground">No seasons created yet.</p>
                )}
                {seasonsData.seasons.length > 0 && (
                  <ul className="space-y-2">
                    {seasonsData.seasons.map((s: SeasonEntry) => (
                      <li
                        key={s.id}
                        className="flex items-center justify-between rounded-lg border bg-card p-3 cursor-pointer hover:bg-accent/50 transition-colors"
                        onClick={() => setSelectedSeason(s.id)}
                      >
                        <div>
                          <p className="font-medium">
                            {s.season_name}
                            {s.is_active && (
                              <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full ml-2">
                                Active
                              </span>
                            )}
                            {s.is_completed && (
                              <span className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded-full ml-2">
                                Completed
                              </span>
                            )}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Season {s.season_number} ·{" "}
                            {new Date(s.start_date).toLocaleDateString()} —{" "}
                            {new Date(s.end_date).toLocaleDateString()}
                          </p>
                        </div>
                        <span className="text-sm text-muted-foreground">
                          {s.total_participants} participants
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
}
